import React from 'react';
import { IntelligentBackdrop } from './IntelligentBackdrop';
import { motion, useReducedMotion } from 'framer-motion';
import { Sparkles, BookOpen, Mic, Building2, ArrowUp } from 'lucide-react';
import { Reveal, Stagger, StaggerItem, EASE } from './LandingMotion';

const FEATURES = [
  {
    icon: BookOpen,
    title: 'Every answer is cited',
    desc: 'Bolt links the exact document and page — never a guess you have to second-guess.',
  },
  {
    icon: Mic,
    title: 'Voice or text, from the field',
    desc: 'Ask hands-free in English or Spanish, gloves on, halfway up a lift.',
  },
  {
    icon: Building2,
    title: 'Grounded in your jobsite',
    desc: 'Reads your live crew, permits, schedule, and uploaded plans — not the open web.',
  },
];

// Signature chat-build variants. Each block defines its own motion so the
// outer <Stagger> can sequence differentiated entrances (slide / rise+blur /
// pop). Children inherit the container's "hidden"/"visible" labels.
const userBubbleVariants = {
  hidden: { opacity: 0, x: 16 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.5, ease: EASE } },
};
const answerVariants = {
  hidden: { opacity: 0, y: 14, filter: 'blur(8px)' },
  visible: { opacity: 1, y: 0, filter: 'blur(0px)', transition: { duration: 0.6, ease: EASE } },
};
const typingVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: EASE } },
};
const chipVariants = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.4, ease: EASE } },
};
// Reduced-motion: collapse every block to a plain fade, no transforms.
const fadeOnly = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.2 } },
};

export const IntelligenceSection = () => {
  const reduce = useReducedMotion();
  const userV = reduce ? fadeOnly : userBubbleVariants;
  const answerV = reduce ? fadeOnly : answerVariants;
  const typingV = reduce ? fadeOnly : typingVariants;
  const chipV = reduce ? fadeOnly : chipVariants;

  return (
    <section className="py-24 md:py-32 relative overflow-hidden bg-white">
      <IntelligentBackdrop />
      <div className="relative z-10 w-full max-w-[1200px] mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">

          {/* Left: Copy */}
          <div className="lg:col-span-5">
            <Reveal blur>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FF6A00]/10 border border-[#FF6A00]/20 text-[#E25C00] text-xs font-mono uppercase tracking-widest mb-6">
                <Sparkles size={12} />
                <span>Bolt · AI Copilot</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-[#1C1C1E] mb-6 leading-[1.1]">
                Ask Bolt anything.
                <br />
                <span className="text-[#FF6A00]">It speaks construction.</span>
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed font-light mb-10 max-w-md">
                Type or talk. Bolt answers from the blueprints, specs, and permits
                you’ve uploaded — and shows you exactly where every answer came from.
              </p>
            </Reveal>

            <Stagger className="space-y-6">
              {FEATURES.map((f) => (
                <StaggerItem key={f.title} className="flex gap-4">
                  <div className="w-10 h-10 shrink-0 rounded-lg bg-white border border-black/10 flex items-center justify-center">
                    <f.icon size={18} className="text-[#FF6A00]" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-[#1C1C1E] mb-1">{f.title}</h3>
                    <p className="text-sm text-slate-600 leading-relaxed">{f.desc}</p>
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
          </div>

          {/* Right: Bolt chat panel */}
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, ease: EASE }}
              className="relative"
            >
              {/* Gentle ambient float — separate wrapper so it composes with the
                  entrance above instead of overriding it. Off under reduced motion. */}
              <motion.div
                className="relative"
                animate={reduce ? undefined : { y: [0, -4, 0] }}
                transition={reduce ? undefined : { duration: 6, repeat: Infinity, ease: 'easeInOut' }}
              >
              {/* Ambient glow */}
              <div className="absolute -inset-4 bg-[#FF6A00]/10 blur-3xl rounded-full opacity-40 pointer-events-none" />

              <div className="relative rounded-2xl bg-white vh-edge-light overflow-hidden">
                {/* Window chrome */}
                <div className="flex items-center justify-between px-5 py-3.5 border-b border-black/[0.06] bg-[#F6F5F2]">
                  <div className="flex items-center gap-2.5">
                    <div className="w-7 h-7 rounded-lg bg-[#FF6A00] flex items-center justify-center shadow-sm">
                      <Sparkles size={15} className="text-white" />
                    </div>
                    <span className="text-sm font-semibold text-[#1C1C1E]">Bolt</span>
                  </div>
                  <div className="flex items-center gap-2 text-[11px] text-slate-600">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                    Reading your plan set
                  </div>
                </div>

                {/* Conversation — builds itself on scroll-in (viewport once).
                    Outer Stagger sequences each block; blocks carry their own
                    differentiated motion via custom variants. */}
                <Stagger className="px-5 py-6 space-y-5" stagger={0.18} delayChildren={0.15}>
                  {/* User message — slides in from the right */}
                  <motion.div variants={userV} className="flex justify-end">
                    <div className="max-w-[78%] rounded-2xl rounded-tr-sm bg-[#FF6A00] px-4 py-2.5 text-sm text-white shadow-sm">
                      What’s the fire rating on the corridor walls?
                    </div>
                  </motion.div>

                  {/* Bolt answer — rises + blur-in */}
                  <motion.div variants={answerV} className="flex gap-3">
                    <div className="w-8 h-8 shrink-0 rounded-lg bg-[#FF6A00]/15 border border-[#FF6A00]/20 flex items-center justify-center">
                      <Sparkles size={15} className="text-[#FF6A00]" />
                    </div>
                    <div className="max-w-[85%] space-y-3">
                      <div className="rounded-2xl rounded-tl-sm bg-white border border-black/[0.06] px-4 py-3 text-sm text-slate-700 leading-relaxed">
                        Corridor walls are{' '}
                        <span className="text-[#FF6A00] font-semibold">2-hour fire-rated</span>{' '}
                        (UL&nbsp;U419), confirmed against the architectural set. The
                        rated assembly carries through to the stair enclosure.
                      </div>
                      {/* Citation chips — nested stagger, scale-pop in */}
                      <Stagger className="flex flex-wrap gap-2" stagger={0.1} delayChildren={0.1}>
                        <motion.span
                          variants={chipV}
                          whileHover={reduce ? undefined : { y: -2, scale: 1.02 }}
                          transition={{ duration: 0.2, ease: EASE }}
                          className="inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-md bg-black/[0.04] border border-black/10 text-slate-600"
                        >
                          <BookOpen size={11} className="text-slate-600" />
                          Spec 09 29 00 · p.12
                        </motion.span>
                        <motion.span
                          variants={chipV}
                          whileHover={reduce ? undefined : { y: -2, scale: 1.02 }}
                          transition={{ duration: 0.2, ease: EASE }}
                          className="inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-md bg-black/[0.04] border border-black/10 text-slate-600"
                        >
                          <BookOpen size={11} className="text-slate-600" />
                          A-501 Wall Types
                        </motion.span>
                      </Stagger>
                    </div>
                  </motion.div>

                  {/* Typing indicator */}
                  <motion.div variants={typingV} className="flex gap-3">
                    <div className="w-8 h-8 shrink-0 rounded-lg bg-[#FF6A00]/15 border border-[#FF6A00]/20 flex items-center justify-center">
                      <Sparkles size={15} className="text-[#FF6A00]" />
                    </div>
                    <div className="flex items-center gap-1.5 rounded-2xl rounded-tl-sm bg-white border border-black/[0.06] px-4 py-3.5">
                      {[0, 1, 2].map((d) => (
                        <motion.span
                          key={d}
                          className="w-1.5 h-1.5 rounded-full bg-slate-500"
                          animate={reduce ? undefined : { opacity: [0.3, 1, 0.3] }}
                          transition={reduce ? undefined : { duration: 1.2, repeat: Infinity, delay: d * 0.2 }}
                        />
                      ))}
                    </div>
                  </motion.div>
                </Stagger>

                {/* Input bar */}
                <div className="px-5 pb-5 pt-1">
                  <div className="flex items-center gap-3 rounded-xl bg-white border border-black/10 px-4 py-2.5">
                    <Mic size={16} className="text-slate-500 shrink-0" />
                    <span className="text-sm text-slate-500 flex-1">Ask about specs, RFIs, schedule…</span>
                    <motion.div
                      whileHover={reduce ? undefined : { scale: 1.08 }}
                      whileTap={reduce ? undefined : { scale: 0.94 }}
                      transition={{ duration: 0.2, ease: EASE }}
                      className="w-7 h-7 rounded-lg bg-[#FF6A00] flex items-center justify-center shadow-sm"
                    >
                      <ArrowUp size={15} className="text-white" />
                    </motion.div>
                  </div>
                </div>
              </div>
              </motion.div>
            </motion.div>
          </div>

        </div>
      </div>
    </section>
  );
};
